import React, { ChangeEvent, useState, useRef, useEffect } from "react";
import { BE_BASE_URL } from "../../utils/config";
import { axiosInstance } from "../../provider/authProvider";
import { AxiosError } from "axios";
import styled from "styled-components";
import Label from "../../components/label";
import Button from "../../components/button/button";
import Suggerimento from "../../components/tooltip/suggerimento";
import { Icons } from "../../constants/icons";
import { useTranslation } from "react-i18next";
import { CircleX } from "lucide-react";
import { Color } from "../../constants/color";
import Title from "../../components/title";
import * as Ariakit from "@ariakit/react";
import { CompanyTrainingNeed } from "../../types";
import { Loader } from "../../components/loader";
import { TrainingNeedsLog } from "../../types";
import { format, set } from "date-fns";
import { url } from "inspector";
import { toast, ToastContainer, Bounce } from "react-toastify";
import { avvisoUnico, messaggioDiErrore } from "../../features/notifiche/avvisi";
import {
  ricalcoloCorsiMancantiInCorso,
  tabellaDalSalvataggio,
  valoriDeiCampi,
} from "../../features/fabbisogno/salvataggio";
import {
  SOLO_CIFRE_ATTRIBUTO,
  useSoloCifre,
} from "../../features/form/soloCifre";
import { useSolaLettura } from "../../features/user/solaLettura";

const RowLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  grid-gap: 0.6em;
  padding: 0.5em 1em;
  text-align: center;
  align-items: center;
`;

const THead = styled(RowLayout)`
  font-weight: bold;
  text-transform: uppercase;
  color: var(--blue300);
`;

const TRow = styled(RowLayout)`
  color: var(--blue500);
  font-weight: bold;
  text-transform: uppercase;
  text-wrap: balance;
  border: 1px solid var(--grey800);
  border-radius: 8px;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2em;
  padding: 0.2em 1.2em;
`;

const UploadContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 50%;
  padding: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  margin-top: 3rem;
`;

const FabbisognoContainer = styled.form`
  margin: 2rem 5rem 2rem 5rem;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

interface NameContainerProps extends React.HTMLAttributes<HTMLSpanElement> {
  alt?: string;
}

const NameWrapper: React.FC<NameContainerProps> = ({
  alt,
  children,
  ...props
}) => (
  <NameContainer title={alt} {...props}>
    {children}
  </NameContainer>
);

const NameContainer = styled.span<NameContainerProps>`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${Color.LIGHT_BLUE};
  font-weight: bold;
  font-family: inherit;
  font-size: 0.85rem;
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: auto;
`;

const NameWrapperContainer = styled.div`
  display: flex;
  flex-direction: row; // Mantiene label e name together in un single row
  align-items: center; // Allinea verticalmente gli elementi
  flex-grow: 1; // Permette di adattarsi in base al contenuto
  min-width: 0; // Importante per evitare overflow del text
`;

const InputField = styled.div`
  font-family: "Avenir Next", sans-serif;
  font-weight: bold;
  color: ${Color.BLUE};
  width: 100%;
  & > label {
    padding-left: 1rem;
    padding-bottom: 0.2rem;
  }

  & > input {
    padding: 0.5rem 1rem;
    border: 2px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    font-size: 1rem;
  }

  & > textarea {
    padding: 0.5rem 1rem;
    border: 2px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    font-size: 1rem;
    overflow-y: auto;
  }
`;

interface LoadingState {
  show: boolean;
  message: string;
  startProcess: boolean;
}

export async function fetchMappedFabbisognoData(
  setLoading: (state: LoadingState) => void
) {
  setLoading({
    show: true,
    message: "Caricamento in corso...",
    startProcess: false,
  });
  try {
    const response = await axiosInstance.get(
      `${BE_BASE_URL}/company-training-needs`
    );
    if (response.status === 200) {
      const trainingData: CompanyTrainingNeed[] = response.data;
      return {
        inclusi: trainingData.map((item) => ({
          id: item.id,
          title: item.courseTitle,
          fabbisogno: item.trainingNeed,
          attivi: item.active,
          dafare: item.pending,
          subscribed: item.subscribed,
          associatedCourseTitle: item.associatedCourseTitle,
        })),
        esclusi: [],
      };
    }
  } catch (error) {
    // console.error("Errore durante il recupero e la mappatura dei documenti.");
  } finally {
    setLoading({ show: false, message: "", startProcess: false });
  }

  return { inclusi: [], esclusi: [] };
}

/*
 Identita' dell'avviso di esito del salvataggio: a schermo ne resta sempre e solo l'ultimo,
 cosi' un errore gia' corretto non sopravvive accanto alla conferma che lo smentisce.
*/
const AVVISO_FABBISOGNO = "fabbisogno-salvataggio";

const TrainingNeedsPage: React.FC = () => {
  const { t } = useTranslation();
  // In sola lettura (abbonamento disattivato, ODOO 300) il fabbisogno si consulta e si scarica,
  // ma non si carica né si valida: il backend rifiuterebbe.
  const solaLettura = useSolaLettura();
  const [file, setFile] = useState<File | null>(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fabbisognoData, setFabbisognoData] = useState<CompanyTrainingNeed[]>(
    []
  );
  const [editTrainingNeed, setEditTrainingNeed] = useState<{
    [key: number]: number;
  }>({});
  const [isTableEdited, setIsTableEdited] = useState(false);
  const [loading, setLoading] = useState({
    show: true,
    message: "Caricamento in corso...",
    startProcess: false,
  });
  const [trainingNeedsLog, setTrainingNeedsLog] = useState<TrainingNeedsLog>();
  /*
   Il salvataggio ha un'attesa sua, distinta da `loading`: `loading.show` copre la pagina con
   un velo, e `loading.startProcess` toglie di mezzo la tabella per l'elaborazione che segue il
   caricamento da file, che dura minuti. Il salvataggio dura una frazione di secondo e non
   merita nessuna delle due: la tabella resta dove sta, con un'indicazione accanto al pulsante.
   Senza una distinzione, i due percorsi si scambiavano l'indicazione l'uno con l'altro.
  */
  const [salvataggioInCorso, setSalvataggioInCorso] = useState(false);
  /*
   I campi del fabbisogno non sono controllati: partono da `defaultValue`, che React legge una
   volta sola al montaggio. Dopo un salvataggio il valore giusto e' quello che il backend ha
   scritto — puo' differire da quanto digitato, per esempio 2,5 salvato come 2 — e l'unico modo
   di farlo comparire senza ricaricare la pagina e' rimontare i campi. Questo contatore, messo
   nella `key`, e' cio' che li rimonta: cambia solo dopo un salvataggio riuscito, quindi mentre
   si scrive nessun campo perde ne' il valore ne' il fuoco.
  */
  const [versioneTabella, setVersioneTabella] = useState(0);
  /*
   Le caselle del fabbisogno accettano solo cifre, e l'ascolto e' uno solo per tutta la
   tabella: `beforeinput` risale il DOM, quindi il modulo sente cio' che accade in ognuna
   delle cinquantadue. Vedi `features/form/soloCifre.ts`.
  */
  const caselleSoloCifre = useSoloCifre<HTMLFormElement>();

  useEffect(() => {
    fetchTrainingNeedsLog();
    fetchTrainingNeeds();
  }, [isTableEdited, isFileUploaded]);

  const fetchTrainingNeeds = async () => {
    setLoading({
      show: true,
      message: "Caricamento in corso...",
      startProcess: false,
    });
    try {
      const response = await axiosInstance.get(
        `${BE_BASE_URL}/company-training-needs`
      );
      if (response.status === 200) {
        const data: CompanyTrainingNeed[] = response.data;
        setFabbisognoData(data);
        const initialValues = data.reduce(
          (acc, row) => ({ ...acc, [row.id]: row.trainingNeed }),
          {}
        );
        setEditTrainingNeed(initialValues);
        setIsTableEdited(false);
      }
    } catch (e) {
      toast.error(
        messaggioDiErrore(e, "Errore durante il recupero dei documenti."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        }
      );
    } finally {
      setLoading({ show: false, message: "", startProcess: false });
    }
  };

  const fetchTrainingNeedsLog = async () => {
    try {
      const response = await axiosInstance.get(
        `${BE_BASE_URL}/training-need-logs`
      );
      if (response.data) {
        setTrainingNeedsLog(response.data);
      }
    } catch (e) {
      toast(messaggioDiErrore(e, "Errore durante il recupero dei documenti."));
    }
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    if (target.files) {
      const uploadedFile = target.files[0];
      setFile(uploadedFile);
    }
  };

  const handleClickRef = () => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = ".xlsx, xls";
      fileInputRef.current.click();
    }
  };

  interface ErrorResponse {
    message?: string;
  }

  const handleFileProcess = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (solaLettura) return;
    setLoading({
      show: false, // Non mostrare il loader overlay bloccante
      message: "", // Il messaggio sarà gestito dal flag startProcess o dai toast
      startProcess: true, // Indica che un processo è iniziato in background
    });

    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      axiosInstance
        .post(`${BE_BASE_URL}/company-training-needs`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          toast.success("Documento caricato con successo", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
          setIsFileUploaded(true); // Questo triggererà l'useEffect che aggiornerà i dati e resetterà startProcess
          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          // Non è necessario setLoading({ show: false, message: "", startProcess: false }); qui,
          // perché isFileUploaded triggera useEffect che chiama fetchTrainingNeeds,
          // il quale ha un blocco finally per resettare lo stato di caricamento.
        })
        .catch((error) => {
          const axiosError = error as AxiosError;
          let errorMessage = "Errore durante il salvataggio del file.";
          if (axiosError.response) {
            const data = axiosError.response.data as ErrorResponse;
            if (data && data.message) {
              errorMessage = data.message;
            }
          } else if (axiosError.message) {
            errorMessage = axiosError.message;
          }
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
          setLoading({ show: false, message: "", startProcess: false }); // Resetta lo stato di caricamento in caso di errore
        });
    } else {
      toast.info("Nessun file selezionato.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      setLoading({ show: false, message: "", startProcess: false }); // Resetta lo stato di caricamento se nessun file è selezionato
    }
  };

  const handleDownloadExport = async () => {
    try {
      const response = await axiosInstance({
        url: BE_BASE_URL + "/company-training-needs/download",
        method: "GET",
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Fabbisogno-export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      //console.error("Errore durante il download dell'export:", error);
    }
  };

  /*
   Salvataggio del fabbisogno formativo.

   Il `try/catch` che stava qui non intercettava niente: avvolgeva una catena di promise, e una
   promise che si rifiuta non lancia dentro al blocco che l'ha creata. La richiesta poteva
   fallire — 400, 500, rete assente — senza che a schermo comparisse nulla, e il riuscito era
   indistinguibile dal fallito perche' nemmeno quello diceva niente. La gestione sta nel
   `.catch()` della catena, ed e' l'unico posto dove puo' stare.

   Il registro (`training-need-logs`) fa parte del salvataggio: e' concatenato, non lanciato di
   lato, altrimenti un suo errore resterebbe di nuovo fuori dalla catena.

   La risposta del PUT e' la tabella intera gia' ricalcolata, e prende il posto dello stato:
   prima veniva buttata via e i campi ripartivano dai dati caricati all'apertura della pagina,
   cosi' accanto alla conferma verde si continuava a leggere il numero di prima finche' non si
   ricaricava (BUG-25). Non serve ne' una seconda chiamata ne' un ricarico.
  */
  const handleBatchSaveAsync = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Il pulsante durante il salvataggio e' `aria-disabled`, non `disabled`: resta raggiungibile
    // e annunciabile, quindi il secondo invio va fermato qui.
    if (salvataggioInCorso || solaLettura) return;
    setSalvataggioInCorso(true);
    const formData = new FormData(event.currentTarget);
    const payload = fabbisognoData.map((row) => ({
      id: row.id,
      trainingNeed: parseInt(
        formData.get(`trainingNeed-${row.id}`) as string,
        10
      ),
    }));

    axiosInstance
      .put(`${BE_BASE_URL}/company-training-needs`, payload)
      .then((risposta) =>
        axiosInstance
          .post(
            `${BE_BASE_URL}/training-need-logs`,
            {},
            { headers: { "Content-Type": "application/json" } }
          )
          .then(() => risposta.data)
      )
      .then((salvato) => {
        const tabella = tabellaDalSalvataggio(salvato);
        if (tabella) {
          setFabbisognoData(tabella);
          setEditTrainingNeed(valoriDeiCampi(tabella));
          setVersioneTabella((versione) => versione + 1);
        } else {
          // Forma inattesa — un backend che risponde ancora com'era: si rilegge, che e' lento
          // ma vero, invece di lasciare a schermo numeri che non sono quelli salvati.
          setIsTableEdited(true);
        }
        // Il salvataggio timbra il registro: senza questo la riga «Validato il ... da ...»
        // resterebbe ferma alla data precedente.
        fetchTrainingNeedsLog();
        avvisoUnico(
          AVVISO_FABBISOGNO,
          "success",
          /*
           Del ricalcolo in sottofondo si dice una frase, dentro l'avviso che c'e' gia'.
           I numeri di questa pagina sono definitivi e non c'e' niente da riprovare: l'unica
           cosa che puo' essere indietro e' l'elenco dei corsi mancanti, che si vede altrove e
           che si rilegge da se' quando ci si arriva. Un secondo avviso, o peggio un blocco,
           chiederebbe attenzione per una cosa che nella quasi totalita' dei casi si e' gia'
           sistemata prima che qualcuno ci vada.
          */
          ricalcoloCorsiMancantiInCorso(salvato)
            ? `${t("training-needs-salvataggio-riuscito")} ${t(
                "training-needs-ricalcolo-corsi-mancanti"
              )}`
            : t("training-needs-salvataggio-riuscito")
        );
      })
      .catch((error) => {
        avvisoUnico(
          AVVISO_FABBISOGNO,
          "error",
          error?.response?.data?.message ||
            t("training-needs-salvataggio-errore")
        );
      })
      // In entrambi i casi l'attesa e' finita: senza questo, "Salvataggio in corso" resta a
      // schermo per sempre, e da solo e' gia' un esito indistinguibile.
      .finally(() => {
        setSalvataggioInCorso(false);
      });
  };

  const nonSubscribedCourses = fabbisognoData
    .filter((row) => !row.subscribed)
    .map((row) => row.courseTitle);

  return (
    <>
      <ToastContainer />
      <PageWrapper>
        <Loader show={loading.show}>{loading.message}</Loader>
        <Title text={t("user-training-needs-title")} />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <UploadContainer>
            <Label
              text={t("user-training-needs-upload-label")}
              style={{ minWidth: "25%" }}
            ></Label>
            <NameWrapperContainer>
              <NameWrapper alt={file?.name}>{file?.name}</NameWrapper>
              {file && (
                <button
                  aria-label={`Togli ${file.name} dalla coda`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "red",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    justifyContent: "center",
                    flexShrink: "0",
                  }}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  <CircleX width={18} height={18} />
                </button>
              )}
            </NameWrapperContainer>
            <ButtonWrapper>
              <Suggerimento testo={t("training-needs-aiuto-seleziona")}>
                <Button
                  id="file-upload"
                  label={t("user-main-info-select-button")}
                  color={Color.GREEN}
                  icon={Icons.BUTTONS.SEARCH}
                  onClick={handleClickRef}
                />
              </Suggerimento>
              <input
                id="file-upload"
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
              <Suggerimento
                testo={t(solaLettura ? "sola-lettura-comando" : "training-needs-aiuto-carica")}
              >
                <Button
                  id="file-process"
                  label={t("user-main-info-upload-button")}
                  color={Color.BLUE}
                  icon={Icons.BUTTONS.UPLOAD}
                  onClick={handleFileProcess}
                  disabled={solaLettura}
                />
              </Suggerimento>
            </ButtonWrapper>
          </UploadContainer>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            marginRight: "5rem",
            marginTop: "1rem",
          }}
        >
          <Suggerimento testo={t("training-needs-aiuto-scarica")}>
            <Button
              label="Scarica Fabbisogno"
              color={Color.GREEN}
              icon={Icons.BUTTONS.DOWNLOAD}
              onClick={() => handleDownloadExport()}
            />
          </Suggerimento>
        </div>
        <FabbisognoContainer
          ref={caselleSoloCifre}
          onSubmit={handleBatchSaveAsync}
          aria-busy={salvataggioInCorso}
        >
          {/*
          <Ariakit.DialogHeading className="heading">
            Fabbisogno formativo calcolato
          </Ariakit.DialogHeading>
          */}
          {loading.startProcess ? (
            <div
              style={{
                margin: "1rem",
                textAlign: "center",
                fontSize: "1.2em",
              }}
            >
              <p
                style={{
                  fontSize: "1.5em",
                  fontWeight: "bold",
                  color: Color.BLUE,
                  padding: "0.5em 0em",
                }}
              >
                Elaborazione fabbisogno in corso.
              </p>
              <p>
                Potete continuare a lavorare all'interno del portale e tornare
                tra qualche minuto per vedere i risultati dell'elaborazione.
              </p>
            </div>
          ) : (
            <>
              <DialogContent>
                {/* TITOLI */}
                <THead>
                  <p>Titolo Corso</p>
                  <p>Fabbisogno formativo</p>
                  <p>Svolti</p>
                  <p>Da fare</p>
                  <p></p>
                </THead>
                {/* ROWS TABLE */}
                {fabbisognoData.map((row) => (
                  <TRow
                    key={row.id}
                    style={{
                      backgroundColor: row.subscribed
                        ? "var(--grey100)"
                        : "lightcoral",
                    }} // Colore di sfondo
                  >
                    <p style={{ textAlign: "left" }}>
                      {row.associatedCourseTitle}
                    </p>
                    <div>
                      <InputField>
                        <input
                          // La `key` porta la versione della tabella: dopo un salvataggio il
                          // campo si rimonta e rilegge `defaultValue`, cioe' il numero che il
                          // backend ha davvero scritto. Vedi il commento su `versioneTabella`.
                          key={`trainingNeed-${row.id}-${versioneTabella}`}
                          type="number"
                          // Il fabbisogno e' un conteggio di persone: intero e mai negativo.
                          // Senza questi due attributi il campo accettava -5 e 3,7, e il
                          // valore finiva nei contatori di conformita'. Il backend rifiuta
                          // comunque i negativi: qui si evita all'operatore di arrivarci.
                          min={0}
                          step={1}
                          /*
                           `min` e `step` rendono il campo non valido, ma la virgola si scriveva
                           lo stesso: si leggeva 2,5 a schermo, il salvataggio non partiva e
                           nessuno diceva perche'. Questo attributo fa ascoltare la casella da
                           `useSoloCifre`, che annulla l'inserimento di tutto cio' che non e' una
                           cifra — virgola, punto, segno meno, lettere — prima che il valore
                           cambi, che arrivi dalla tastiera, dall'incolla, dal trascinamento o
                           dalla dettatura. `min` e `step` restano al loro posto, e cosi' il
                           rifiuto del backend: questa e' la porta, non la serratura.
                          */
                          {...{ [SOLO_CIFRE_ATTRIBUTO]: true }}
                          // Trentadue caselle numeriche tutte uguali: senza il titolo del
                          // corso un lettore di schermo non puo' dire a quale appartengano,
                          // ed e' la pagina dove si decide il fabbisogno dell'azienda.
                          aria-label={t("training-needs-campo-fabbisogno", {
                            corso: row.associatedCourseTitle,
                          })}
                          name={`trainingNeed-${row.id}`}
                          defaultValue={row.trainingNeed || 0}
                          // Mentre il salvataggio e' in volo il campo non si modifica, ma
                          // resta nel percorso di tabulazione e conserva il suo nome:
                          // `disabled` lo toglierebbe di li' e un lettore di schermo non
                          // potrebbe piu' raggiungerlo ne' dire a quale corso appartiene.
                          readOnly={salvataggioInCorso}
                          aria-disabled={salvataggioInCorso}
                          style={{
                            width: "40%",
                            padding: "0.2em",
                            textAlign: "end",
                          }}
                        />
                      </InputField>
                    </div>
                    <p>{row.active}</p>
                    <p>{row.pending}</p>
                    <Suggerimento testo={t("training-needs-aiuto-dettaglio")}>
                      <a
                        href={(() => {
                          const params = new URLSearchParams();
                          params.append("corso", row.associatedCourseTitle);
                          return `/attestati?${params.toString()}`;
                        })()}
                      >
                        <Button
                          style={{ marginTop: 0 }}
                          label="DETTAGLIO"
                          color={Color.BLUE}
                        />
                      </a>
                    </Suggerimento>
                  </TRow>
                ))}
                <div
                  style={{
                    marginTop: "1rem",
                    marginBottom: "1rem",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {trainingNeedsLog && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        gap: "0.3rem",
                        color: Color.BLUE,
                        fontStyle: "italic", // Aggiunta per rendere il testo in corsivo
                      }}
                    >
                      <p>
                        Validato il{" "}
                        {trainingNeedsLog?.validationDate
                          ? format(
                              new Date(trainingNeedsLog.validationDate),
                              "dd/MM/yyyy"
                            )
                          : "N/A"}
                      </p>
                      <p>da {trainingNeedsLog?.user.firstName}</p>
                      <p>{trainingNeedsLog?.user.lastName}</p>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.8rem",
                    }}
                  >
                    {/*
                     Che l'operazione sia in corso va detto, non lasciato intuire: anche due
                     secondi di schermo immobile sono indistinguibili da un guasto, ed e' la
                     ragione per cui il cliente ha concluso che il salvataggio non andasse.
                     La regione e' sempre nel DOM e cambia solo di contenuto: un `aria-live`
                     che compare insieme al testo non viene annunciato.
                    */}
                    <span
                      role="status"
                      aria-live="polite"
                      style={{
                        color: Color.BLUE,
                        fontStyle: "italic",
                        fontWeight: "bold",
                      }}
                    >
                      {salvataggioInCorso
                        ? t("training-needs-salvataggio-in-corso")
                        : ""}
                    </span>
                    <Suggerimento
                      testo={t(solaLettura ? "sola-lettura-comando" : "training-needs-aiuto-valida")}
                    >
                      <Button
                        style={{
                          marginTop: 0,
                          opacity: salvataggioInCorso || solaLettura ? 0.5 : 1,
                        }}
                        disabled={solaLettura}
                        label="Valida e Aggiorna"
                        type="submit"
                        // `aria-disabled` e non `disabled`: il pulsante resta raggiungibile con
                        // il tasto di tabulazione e conserva il suo nome, mentre `disabled` lo
                        // toglierebbe dal percorso e chi legge a voce non lo troverebbe piu'.
                        // Il secondo invio lo ferma `handleBatchSaveAsync`.
                        aria-disabled={salvataggioInCorso}
                      />
                    </Suggerimento>
                  </div>
                </div>
              </DialogContent>
              {/* MESSAGGIO PER CORSI NON ABBONATI */}
              {nonSubscribedCourses.length > 0 && (
                <p
                  style={{
                    margin: "1rem",
                    color: "red",
                    textAlign: "start",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  *I corsi evidenziati in rosso non fanno parte del pacchetto di
                  abbonamento acquistato.
                </p>
              )}
            </>
          )}
        </FabbisognoContainer>
      </PageWrapper>
    </>
  );
};

export default TrainingNeedsPage;
